import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import BasicButton, {
  BASIC_BUTTON_COLOR_ACTION,
  BASIC_BUTTON_SIZE_SMALL,
  BASIC_BUTTON_TYPE_OUTLINED,
} from '@divvy-web/skylab.basicbutton'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useAmendSentToSignerApp } from './AmendSentToSignerApp.gql'
import { useResendApplication } from './ResendApplication.gql'

interface AwaitingSignatureBannerProps {
  salesforceCreditId: string
}

/* This status shows if status is awaiting_signature and is not the authorized signer and they are waiting for the auth signer to review and submit the application
 */
const AwaitingSignatureBanner = ({ salesforceCreditId }: AwaitingSignatureBannerProps) => {
  const navigate = useNavigate()
  const showToast = useToast()
  const { track } = useWebTracking()
  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({
    salesforceCreditId,
  })

  useEffect(() => {
    track('se_signup__application_dashboard_action_needed_awaiting_signature__presented')
  }, [track])

  //TODO: make new query and call refetch getapplicationdetails
  const [amendSentToSignerApp, { loading: isLoadingAmendSentToSigner }] = useAmendSentToSignerApp({
    variables: { salesforceCreditId },
    refetchQueries: ['GetCreditApplicationDetails'],
    onCompleted: () => {
      logInfo('amendApplication', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Application amended',
        },
        eventName: 'amendSentToSignerApp',
      })

      navigate(`/app/${salesforceCreditId}/${PATHNAME_CONSTS.REVIEW_PATH}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'amendApplication',
          creditAppId: salesforceCreditId,
          result: 'Application amend failed',
          message: error?.message,
        },
        eventName: 'amendSentToSignerApp',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error amending credit application.'
          id='sputnik.AwaitingSignatureStatusTile__kBwmHX'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  const [resendApplication, { loading: isLoadingResendApplication }] = useResendApplication({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
    onCompleted: () => {
      logInfo('resendApplication', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Successfully resent the credit app to signer',
        },
        eventName: 'resendApplicationSuccess',
      })

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Successfully resent the credit application to authorized signer'
          id='sputnik.AwaitingSignatureBanner__dW11Qw'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'Resend to Signer',
          creditAppId: salesforceCreditId,
          result: 'Failed to resend application to signer',
          message: error?.message,
        },
        eventName: 'resendApplicationError',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error resending application for signature'
          id='sputnik.AwaitingSignatureBanner__dA/cyp'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
  })

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Informative}
        subtext={
          <>
            <FormattedMessage
              defaultMessage='We’ve notified the authorized signer to review and submit the application. <link>Amend application</link>'
              id='sputnik.AwaitingSignatureBanner__pRfMbC'
              values={{
                link: (chunks) => (
                  <TextLink
                    disabled={isLoadingAmendSentToSigner}
                    size={TextLinkSizes.SMALL}
                    onClick={() => {
                      trackCollaboratorEvent(
                        'se_signup__application_dashboard_action_needed_amend_application__fired',
                        (role) => ({
                          person_type: role,
                        }),
                      )
                      amendSentToSignerApp()
                    }}
                  >
                    {chunks}
                  </TextLink>
                ),
              }}
            />

            <BasicButton
              color={BASIC_BUTTON_COLOR_ACTION}
              disabled={isLoadingResendApplication}
              size={BASIC_BUTTON_SIZE_SMALL}
              type={BASIC_BUTTON_TYPE_OUTLINED}
              onClick={() => {
                trackCollaboratorEvent(
                  'se_signup__application_dashboard_action_needed_resend_application__fired',
                  (role) => ({
                    person_type: role,
                  }),
                )
                resendApplication()
              }}
            >
              <FormattedMessage
                defaultMessage='Re-send application'
                id='sputnik.AwaitingSignatureBanner__BB1eP1'
              />
            </BasicButton>
          </>
        }
        title={
          <FormattedMessage
            defaultMessage='Awaiting signature'
            id='sputnik.AwaitingSignatureBanner__1JRfgv'
          />
        }
      />
    </div>
  )
}

export default AwaitingSignatureBanner
